import React, { useState, useEffect, useRef } from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import Person from "@material-ui/icons/Person";
import Search from "@material-ui/icons/Search";
import Add from "@material-ui/icons/Add";
import Remove from "@material-ui/icons/Remove";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import PersonAdd from "@material-ui/icons/PersonAdd";
import AttachMoney from "@material-ui/icons/AttachMoney";
import CreditCard from "@material-ui/icons/CreditCard";
import AccountBalance from "@material-ui/icons/AccountBalance";
import Store from "@material-ui/icons/Store";
import Check from "@material-ui/icons/Check";

// Icons & Network
import DoobiHeader from "components/Headers/DoobiHeader";
import BranchBar from "./BranchBar";
import config from "../../../config";
import { BUSINESS_INFO, pad2, formatReadyDate, buildReceiptBody, writeReceiptToWindow } from "./receipt";
import {
    DEFAULT_POS_EXPRESS_PERCENTAGE,
    parsePrice,
    parsePercentage,
    getServiceTypesForItem,
    buildDisplayItems,
    getExpressPrice as getExpressPriceShared,
    getServiceTypeName as getServiceTypeNameShared,
    getItemCategoryLabel as getItemCategoryLabelShared
} from "./posCatalog";

const POS_EXPRESS_PERCENTAGE_PARAM_KEY = "POS_EXPRESS_PERCENTAGE";
// POS bills carry their own VAT rate, separate from the app's "VAT_PERCENTAGE", so a branch can
// be taxed differently from the customer app. Falls back to the app rate while the POS-specific
// admin-param has not been configured for the country yet.
const POS_VAT_PERCENTAGE_PARAM_KEY = "POS_VAT_PERCENTAGE";
const APP_VAT_PERCENTAGE_PARAM_KEY = "VAT_PERCENTAGE";
const formatBd = (value) => `${(Number(value) || 0).toFixed(3)} BD`;

export const posStyles = (theme) => ({
    cardRoot: {
        marginBottom: "1.5rem",
    },
    itemCard: {
        cursor: "pointer",
        transition: "all 0.2s",
        border: "2px solid transparent",
        borderRadius: "12px",
        height: "100%",
        // `&&` doubles the class in the selector purely for specificity. The
        // theme's .MuiCard-root / .MuiCardContent-root rules are injected after
        // makeStyles, so at equal specificity they win on order -- a plain
        // `marginBottom: 0` here is silently ignored.
        //
        // The global MuiCard rule adds 30px below every card, which suits
        // full-width dashboard cards but doubles up with the Grid's own spacing
        // here. The global 1.5rem CardContent padding puts 48px of vertical
        // padding around ~70px of content, which is what leaves these tiles
        // half empty.
        "&&": {
            marginBottom: 0,
        },
        "&& .MuiCardContent-root": {
            padding: "12px",
        },
        // MUI's own `.MuiCardContent-root:last-child` sets paddingBottom: 24px
        "&& .MuiCardContent-root:last-child": {
            paddingBottom: "12px",
        },
        "&:hover": {
            borderColor: theme.palette.primary.main,
            transform: "translateY(-2px)",
            boxShadow: "0 4px 20px 0 rgba(0,0,0,.12)",
        },
    },
    itemIcon: {
        width: "50px",
        height: "50px",
        objectFit: "contain",
        marginRight: "12px"
    },
    searchField: {
        marginBottom: "1.5rem",
    },
    serviceChip: {
        margin: "8px",
        fontWeight: "600",
        padding: "12px 8px",
        height: "auto",
        borderRadius: "10px",
        fontSize: "0.95rem",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
            backgroundColor: theme.palette.primary.main + " !important",
            color: "#fff !important",
            borderColor: theme.palette.primary.main,
            "& .MuiChip-label": {
                color: "#fff",
            }
        }
    },
    paymentButton: {
        margin: "0 5px",
        minWidth: "80px",
        fontWeight: "bold",
    },
    basketItem: {
        borderBottom: "1px solid #f2f2f2",
        padding: "12px 0",
        "&:last-child": {
            borderBottom: "none"
        }
    },
    qtyBtn: {
        padding: "4px",
        border: "1px solid #e9ecef",
        borderRadius: "4px",
        margin: "0 8px"
    },
    fullHeightCard: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "#fff",
        borderRadius: "15px",
        boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .15)",
    },
    scrollableContent: {
        flex: 1,
        overflowY: "auto",
        paddingRight: "10px",
        minHeight: 0,
        "&::-webkit-scrollbar": {
            width: "5px",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e9ecef",
            borderRadius: "10px",
        },
    },
    selectionBlock: {
        padding: "12px",
        borderRadius: "8px",
        border: "1px dashed #ced4da",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "1rem",
        transition: "all 0.2s",
        "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: "#f8f9fe"
        }
    },
    receiptDesk: {
        backgroundColor: "#eef1f6",
        borderRadius: "14px",
        padding: "22px 12px",
        maxHeight: "68vh",
        overflowY: "auto",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#cfd5e3", borderRadius: "10px" },
    },
    receiptPaper: {
        backgroundColor: "#fff",
        borderRadius: "6px",
        padding: "22px 18px",
        margin: "0 auto",
        maxWidth: "320px",
        boxShadow: "0 6px 22px 0 rgba(0,0,0,.12)",
    },
    totalBanner: {
        background: `linear-gradient(87deg, ${theme.palette.primary.main} 0, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
        borderRadius: "14px",
        padding: "18px 22px",
        marginBottom: "22px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 6px 18px 0 rgba(94,114,228,.28)",
    },
    paymentRow: {
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "13px 16px",
        borderRadius: "12px",
        border: "2px solid #e6e8ef",
        backgroundColor: "#fff",
        cursor: "pointer",
        userSelect: "none",
        marginBottom: "12px",
        transition: "all 0.16s ease",
        "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: "#f8f9fe",
        },
    },
    paymentRowSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: "rgba(94,114,228,.07)",
        boxShadow: "0 4px 14px 0 rgba(94,114,228,.18)",
    },
    paymentRowIcon: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "46px",
        height: "46px",
        borderRadius: "12px",
        backgroundColor: "#f0f2f8",
        color: "#8898aa",
        flexShrink: 0,
        transition: "all 0.16s ease",
    },
    paymentRowIconSelected: {
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
    },
    paymentRowLabel: {
        fontWeight: 700,
        fontSize: "1.05rem",
        color: "#32325d",
        flex: 1,
    },
    paymentCheck: {
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        border: "2px solid #cfd5e3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: "transparent",
        transition: "all 0.16s ease",
    },
    paymentCheckSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
    },
});

const useStyles = makeStyles(posStyles);

// Formats a Date into the value expected by an <input type="datetime-local"> (local time).
const toDateTimeLocal = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

function Pos() {
    const classes = useStyles();
    const theme = useTheme();

    const [allServices, setAllServices] = useState([]);
    const [globalServiceTypes, setGlobalServiceTypes] = useState([]);
    const [items, setItems] = useState([]); // Main items with servicesById
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");

    // Dialog state for selecting service type
    const [selectedItem, setSelectedItem] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Payment method dialog
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    // After an order is saved, the admin prints copies from this dialog (customer always,
    // store optional). printData holds the receipt snapshot since the basket is cleared by then.
    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    const [printData, setPrintData] = useState(null);
    // VAT defaults to the configured POS rate but can be overridden per order from the basket.
    // The configured value is kept alongside it so the override only lasts for the order it was
    // typed on -- placing the order resets the rate rather than carrying it to the next customer.
    const [configuredVatPercentage, setConfiguredVatPercentage] = useState(0);
    const [vatPercentage, setVatPercentage] = useState(0);
    const [expressOrder, setExpressOrder] = useState(false);
    const [expressPercentage, setExpressPercentage] = useState(DEFAULT_POS_EXPRESS_PERCENTAGE);

    const PAYMENT_METHODS = [
        { label: "Cash", desc: "Paid in cash", icon: <AttachMoney style={{ fontSize: 26 }} /> },
        { label: "Card", desc: "Credit / debit card", icon: <CreditCard style={{ fontSize: 26 }} /> },
        { label: "Benefit", desc: "BenefitPay", icon: <AccountBalance style={{ fontSize: 26 }} /> },
        { label: "Pay on Collection", desc: "Pay at pickup", icon: <Store style={{ fontSize: 26 }} /> },
    ];

    const [basket, setBasket] = useState([]);
    const [discount, setDiscount] = useState(0);
    const [deliveryCharge, setDeliveryCharge] = useState(0);
    // When the customer can collect their clothes. Defaults to 24h after the order is placed,
    // but the admin can override it before checking out. Shown on the printed receipt.
    const [readyDate, setReadyDate] = useState(() => toDateTimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000)));
    const [readyDateEdited, setReadyDateEdited] = useState(false);
    const prevBasketLen = useRef(0);

    // Customer & Address State
    const [allUsers, setAllUsers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
    const [addressDialogOpen, setAddressDialogOpen] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState("");

    // Unified Customer Form State
    const [customerForm, setCustomerForm] = useState({
        mobileNumber: "",
        firstName: "",
        lastName: "",
        emailAddress: "",
        building: "",
        road: "",
        block: "",
        districtArea: "",
        flatOfficeNo: "",
        shortAddress: ""
    });

    const resetCustomerForm = () => {
        setCustomerForm({
            mobileNumber: "",
            firstName: "",
            lastName: "",
            emailAddress: "",
            building: "",
            road: "",
            block: "",
            districtArea: "",
            flatOfficeNo: "",
            shortAddress: ""
        });
    };

    // Refresh the default ready date to 24h from "now" when a new order starts (first item
    // added to an empty basket), so it tracks order time rather than when the page last loaded.
    // Skips if the admin has manually set a date.
    useEffect(() => {
        if (prevBasketLen.current === 0 && basket.length > 0 && !readyDateEdited) {
            setReadyDate(toDateTimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000)));
        }
        prevBasketLen.current = basket.length;
    }, [basket.length, readyDateEdited]);

    useEffect(() => {
        let isMounted = true;

        const fetchServices = async () => {
            try {
                const token = localStorage.getItem("token");
                const selectedCountry = localStorage.getItem("selectedCountry");
                const tenantId = localStorage.getItem("tenantId");

                const apiUrl = `${config.WS_BASE_URL}app/service/activeForPos`;

                const fetchResponse = await fetch(apiUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': token || '',
                        'Country': selectedCountry || '',
                        'Content-Type': 'application/json',
                        'X-Tenant-Key': tenantId || 'main'
                    }
                });

                const responseData = await fetchResponse.json();

                if (!isMounted) return;

                if (!responseData) {
                    throw new Error("No response data received from API");
                }

                const data = responseData.data || [];
                setAllServices(data);

                // Items are directly in the data array
                // Each item has servicesById array with available service types
                const sortedItems = [...data].sort((a, b) => (a.orderNo || 0) - (b.orderNo || 0));
                setItems(sortedItems);

            } catch (error) {
                console.error("POS: Error in fetchServices:", error);
                if (isMounted) setErrorMsg(error.message || "Failed to load services");
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        const fetchServiceTypes = async () => {
            try {
                const token = localStorage.getItem("token");
                const selectedCountry = localStorage.getItem("selectedCountry");
                const tenantId = localStorage.getItem("tenantId");

                const response = await fetch(`${config.WS_BASE_URL}app/service-type`, {
                    method: 'GET',
                    headers: {
                        'Authorization': token || '',
                        'Country': selectedCountry || '',
                        'Content-Type': 'application/json',
                        'X-Tenant-Key': tenantId || 'main'
                    }
                });

                const responseData = await response.json();
                if (!isMounted) return;

                const data = Array.isArray(responseData?.data)
                    ? responseData.data
                    : Array.isArray(responseData)
                        ? responseData
                        : [];

                setGlobalServiceTypes(data);
            } catch (error) {
                console.error("POS: Error in fetchServiceTypes:", error);
            }
        };

        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem("token");
                const selectedCountry = localStorage.getItem("selectedCountry");
                const tenantId = localStorage.getItem("tenantId");

                const response = await fetch(config.WS_BASE_URL + "app/user", {
                    headers: {
                        'Authorization': token || '',
                        'Country': selectedCountry || '1',
                        'Content-Type': 'application/json',
                        'X-Tenant-Key': tenantId || 'main'
                    }
                });
                const data = await response.json();
                if (data && data.data) {
                    if (isMounted) setAllUsers(data.data);
                }
            } catch (err) {
                console.error("Error fetching users:", err);
            }
        };

        const fetchConfig = async () => {
            try {
                const token = localStorage.getItem("token");
                const selectedCountry = localStorage.getItem("selectedCountry");
                const tenantId = localStorage.getItem("tenantId");

                const response = await fetch(`${config.WS_BASE_URL}app/params`, {
                    headers: {
                        'Authorization': token || '',
                        'Country': selectedCountry || '1',
                        'Content-Type': 'application/json',
                        'X-Tenant-Key': tenantId || 'main'
                    }
                });
                const data = await response.json();
                // POS VAT comes from the "POS_VAT_PERCENTAGE" admin-param (scoped to the country),
                // falling back to the app's "VAT_PERCENTAGE" until the POS one is configured.
                const params = data?.data ?? (Array.isArray(data) ? data : []);
                const vatParam = Array.isArray(params)
                    ? (params.find((p) => p.paramKey === POS_VAT_PERCENTAGE_PARAM_KEY)
                        ?? params.find((p) => p.paramKey === APP_VAT_PERCENTAGE_PARAM_KEY))
                    : null;
                const vat = vatParam?.paramValue;
                if (isMounted && vat != null) {
                    const configuredVat = parseFloat(vat) || 0;
                    setConfiguredVatPercentage(configuredVat);
                    setVatPercentage(configuredVat);
                }

                const expressParam = Array.isArray(params)
                    ? params.find((p) => p.paramKey === POS_EXPRESS_PERCENTAGE_PARAM_KEY)
                    : null;
                if (isMounted) {
                    setExpressPercentage(parsePercentage(expressParam?.paramValue, DEFAULT_POS_EXPRESS_PERCENTAGE));
                }
            } catch (err) {
                console.error("Error fetching config:", err);
            }
        };

        fetchServices();
        fetchServiceTypes();
        fetchUsers();
        fetchConfig();

        return () => {
            isMounted = false;
        };
    }, []);

    // The catalogue logic below is shared with the admin order editor in PosSales.js; these
    // wrappers just bind it to this screen's loaded catalogue and express settings.
    const getExpressPrice = (basePrice, enabled = expressOrder, percentage = expressPercentage) =>
        getExpressPriceShared(basePrice, enabled, percentage);

    const getServiceTypeName = (serviceType) => getServiceTypeNameShared(globalServiceTypes, serviceType);

    const displayItems = buildDisplayItems(items);

    const getItemCategoryLabel = (item) => getItemCategoryLabelShared(item, allServices);

    const getItemServiceTypeLabels = (item) => {
        const seenLabels = new Set();

        return getServiceTypesForItem(item)
            .map((serviceType) => getServiceTypeName(serviceType))
            .filter((label) => {
                if (!label || seenLabels.has(label)) {
                    return false;
                }

                seenLabels.add(label);
                return true;
            });
    };

    // Filter items based on search query and isPerItem=1
    const filteredItems = displayItems.filter(item => {
        const normalizedSearch = searchQuery.toLowerCase();
        const matchesSearch = item.nameEn?.toLowerCase().includes(normalizedSearch) ||
            item.nameAr?.includes(searchQuery) ||
            item.parentCategoryNameEn?.toLowerCase().includes(normalizedSearch) ||
            item.parentCategoryNameAr?.includes(searchQuery);

        return matchesSearch;
    });

    const groupedFilteredItems = filteredItems.reduce((groups, item) => {
        const categoryLabel = getItemCategoryLabel(item);
        const existingGroup = groups.find((group) => group.label === categoryLabel);

        if (existingGroup) {
            existingGroup.items.push(item);
            return groups;
        }

        groups.push({
            label: categoryLabel,
            items: [item]
        });

        return groups;
    }, []);

    // Auto-populate customer form when phone number matches
    useEffect(() => {
        const phone = customerForm.mobileNumber.trim();
        if (phone.length >= 8) {
            // Check if we already have this user in our local allUsers list (optional optimization)
            const match = allUsers.find(u => u.phoneNumber === phone || (u.mobileNumber === phone));
            if (match) {
                setCustomerForm(prev => ({
                    ...prev,
                    firstName: match.firstName || "",
                    lastName: match.lastName || "",
                    emailAddress: match.emailAddress || "",
                    building: match.building || "",
                    road: match.road || "",
                    block: match.block || "",
                    districtArea: match.districtArea || match.districtName || "",
                    flatOfficeNo: match.flatOfficeNo || match.flat || "",
                    shortAddress: match.shortAddress || ""
                }));
            } else {
                // If not found in local state, we can call the search API
                const searchCustomer = async () => {
                    try {
                        const token = localStorage.getItem("token");
                        const selectedCountry = localStorage.getItem("selectedCountry");
                        const tenantId = localStorage.getItem("tenantId");

                        const response = await fetch(`${config.WS_BASE_URL}api/walk-in-customer/search?phone=${phone}`, {
                            headers: {
                                'Authorization': token || '',
                                'Country': selectedCountry || '1',
                                'Content-Type': 'application/json',
                                'X-Tenant-Key': tenantId || 'main'
                            }
                        });

                        if (response.ok) {
                            const found = await response.json();
                            if (found) {
                                setCustomerForm(prev => ({
                                    ...prev,
                                    firstName: found.firstName || "",
                                    lastName: found.lastName || "",
                                    emailAddress: found.emailAddress || "",
                                    building: found.building || "",
                                    road: found.road || "",
                                    block: found.block || "",
                                    districtArea: found.districtArea || "",
                                    flatOfficeNo: found.flatOfficeNo || "",
                                    shortAddress: found.shortAddress || ""
                                }));
                            }
                        }
                    } catch (err) {
                        console.error("Error searching customer:", err);
                    }
                };
                searchCustomer();
            }
        }
    }, [customerForm.mobileNumber, allUsers]);

    const handleConfirmOrder = () => {
        if (!selectedCustomer) {
            alert("Please select or add a customer first");
            return;
        }
        if (basket.length === 0) {
            alert("Basket is empty");
            return;
        }
        setSelectedPaymentMethod(null);
        setPaymentDialogOpen(true);
    };

    const handlePlaceOrder = async (paymentMethod) => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");

            const orderData = {
                walkInCustomerId: selectedCustomer.id,
                subtotal: subtotal,
                discount: discountAmount,
                deliveryCharge: parseFloat(deliveryCharge || 0),
                vat: vatAmount,
                total: grandTotal,
                createdBy: localStorage.getItem("username"),
                paymentMethod: paymentMethod,
                express: expressOrder,
                expressPercentage: expressOrder ? parsePercentage(expressPercentage, DEFAULT_POS_EXPRESS_PERCENTAGE) : 0,
                items: basket.map(item => {
                    const orderItem = {
                        serviceId: item.serviceId,
                        quantity: item.quantity,
                        price: item.price,
                        total: item.price * item.quantity
                    };

                    if (item.serviceTypeId != null) {
                        orderItem.serviceTypeId = item.serviceTypeId;
                    }

                    return orderItem;
                })
            };

            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order`, {
                method: 'POST',
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'Content-Type': 'application/json',
                    'X-Tenant-Key': tenantId || 'main'
                },
                body: JSON.stringify(orderData)
            });

            if (response.ok) {
                const savedOrder = await response.json();
                // Snapshot the receipt now (basket is about to be cleared), then let the admin
                // print the copies they need from the print dialog.
                setPrintData(buildReceiptData(savedOrder?.branchOrderCode || savedOrder?.id));
                setPrintDialogOpen(true);
                setPaymentDialogOpen(false);
                setBasket([]);
                setSelectedCustomer(null);
                setSelectedAddress(null);
                setDiscount(0);
                setDeliveryCharge(0);
                // a per-order VAT override applies to that order only
                setVatPercentage(configuredVatPercentage);
                setExpressOrder(false);
                setReadyDate(toDateTimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000)));
                setReadyDateEdited(false);
                setSelectedPaymentMethod(null);
            } else {
                const result = await response.json();
                throw new Error(result.message || "Failed to place order");
            }
        } catch (error) {
            console.error("Error placing order:", error);
            alert(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Each copy prints as its own job (opened on the button click, so no pop-up blocking),
    // which also makes the printer cut between separately-printed tickets.
    const printReceiptCopy = (copyLabel) => {
        if (!printData) return;
        const win = window.open("", "PRINT", "height=600,width=400");
        writeReceiptToWindow(win, printData, copyLabel);
    };

    const handleSaveCustomer = async () => {
        try {
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");

            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-customer`, {
                method: 'POST',
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'Content-Type': 'application/json',
                    'X-Tenant-Key': tenantId || 'main'
                },
                body: JSON.stringify(customerForm)
            });

            if (response.ok) {
                const userData = await response.json();
                setSelectedCustomer(userData);
                setSelectedAddress({
                    building: customerForm.building,
                    road: customerForm.road,
                    block: customerForm.block,
                    districtArea: customerForm.districtArea,
                    shortAddress: customerForm.shortAddress,
                    flatOfficeNo: customerForm.flatOfficeNo
                });
                setCustomerDialogOpen(false);
            } else {
                const result = await response.json();
                throw new Error(result.message || "Failed to save customer");
            }
        } catch (error) {
            console.error("Error saving customer:", error);
            alert(error.message);
        }
    };

    const handleItemClick = (item) => {
        if (getServiceTypesForItem(item).length === 0) {
            return;
        }
        setSelectedItem(item);
        setDialogOpen(true);
    };

    const handleServiceSelect = (item, serviceType) => {
        const selectedTypeId = serviceType.serviceTypeId ?? serviceType.id;
        const basketId = `${item.id}-${selectedTypeId}`;
        const cleanServiceName = getServiceTypeName(serviceType);
        const usesNewPricingStructure = Array.isArray(item?.servicePricings) && item.servicePricings.length > 0;
        const basePrice = parsePrice(serviceType.price);
        const resolvedPrice = getExpressPrice(basePrice);

        setBasket(prev => {
            const existing = prev.find(i => i.basketId === basketId);
            if (existing) {
                return prev.map(i => i.basketId === basketId ? { ...i, quantity: i.quantity + 1 } : i);
            }
            return [...prev, {
                basketId,
                itemId: item.id,
                serviceId: usesNewPricingStructure ? item.id : serviceType.id,
                serviceTypeId: usesNewPricingStructure ? selectedTypeId : undefined,
                name: item.nameEn,
                serviceName: cleanServiceName,
                basePrice,
                price: resolvedPrice,
                quantity: 1,
                image: item.imageUrl
            }];
        });
        setDialogOpen(false);
        setSelectedItem(null);
    };

    const handleExpressChange = (checked) => {
        setExpressOrder(checked);
        setBasket(prev => prev.map(item => {
            const basePrice = parsePrice(item.basePrice ?? item.price);
            return {
                ...item,
                basePrice,
                price: getExpressPrice(basePrice, checked)
            };
        }));
    };

    const updateQuantity = (basketId, delta) => {
        setBasket(prev => prev
            .map(i => i.basketId === basketId ? { ...i, quantity: i.quantity + delta } : i)
            .filter(i => i.quantity > 0));
    };

    const removeFromBasket = (basketId) => {
        setBasket(prev => prev.filter(i => i.basketId !== basketId));
    };

    const subtotal = basket.reduce((acc, item) => acc + (parseFloat(item.price || 0) * item.quantity), 0);
    // `discount` is entered as a percentage of the subtotal; convert it to a BD amount.
    const discountPercent = Math.min(100, Math.max(0, parseFloat(discount || 0)));
    const discountAmount = subtotal * discountPercent / 100;
    const taxableBase = Math.max(0, subtotal - discountAmount + parseFloat(deliveryCharge || 0));
    // Clamped like the discount above: the input's min/max only constrain the spinner, not what
    // can be typed or pasted into it.
    const vatPercent = Math.min(100, Math.max(0, parseFloat(vatPercentage) || 0));
    const vatAmount = taxableBase * vatPercent / 100;
    const grandTotal = taxableBase + vatAmount;
    const totalPieces = basket.reduce((acc, item) => acc + item.quantity, 0);
    const selectedItemServiceTypes = getServiceTypesForItem(selectedItem);

    const buildReceiptData = (orderNo) => {
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        const hh = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        return {
            orderNo: orderNo != null ? orderNo : "—",
            pieces: totalPieces,
            business: {
                name: localStorage.getItem("laundryName") || BUSINESS_INFO.name,
                address: localStorage.getItem("laundryAddress") || BUSINESS_INFO.address,
                tel: localStorage.getItem("laundryPhone") || BUSINESS_INFO.tel,
            },
            servedBy: localStorage.getItem("username") || "",
            customerName: selectedCustomer
                ? `${selectedCustomer.firstName || ""} ${selectedCustomer.lastName || ""}`.trim()
                : "",
            customerRef: selectedCustomer
                ? (selectedCustomer.mobileNumber || selectedCustomer.phoneNumber || "")
                : "",
            items: basket.map((i) => ({
                name: i.name,
                serviceName: i.serviceName,
                express: expressOrder,
                price: i.price,
                qty: i.quantity,
                total: i.price * i.quantity,
            })),
            subtotal,
            discount: discountAmount,
            deliveryCharge: parseFloat(deliveryCharge || 0),
            vat: vatAmount,
            total: grandTotal,
            paymentMethod: selectedPaymentMethod,
            express: expressOrder,
            expressPercentage: expressOrder ? parsePercentage(expressPercentage, DEFAULT_POS_EXPRESS_PERCENTAGE) : 0,
            date: `${dd}/${mm}/${yy} ${hh}:${min}`,
            readyDate: formatReadyDate(readyDate),
        };
    };

    return (
        <Box style={{
            backgroundColor: "#f8f9fe",
            height: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
        }}>
            <DoobiHeader section="InBuilt POS" subsection="Handle walk-in sales with Built-in POS System" />
            {/* -4.5rem, not -6rem: this page sits under DoobiHeader, whose
                paddingBottom is 4.5rem. -6rem belongs to StatsHeader pages,
                which pad 8rem. Pulling up further than the header is tall
                drags the branch bar above the top of the viewport. */}
            <Box style={{ flex: 1, minHeight: 0, position: 'relative', marginTop: '-4.5rem', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Container maxWidth={false} component={Box} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', paddingBottom: '1rem' }}>
                    <BranchBar dense />
                    <Grid container spacing={3} alignItems="stretch" wrap="nowrap" style={{ flex: 1, minHeight: 0 }}>
                        {/* LEFT COLUMN - Item Search & Selection */}
                        <Grid item xs={12} lg={8} style={{ display: 'flex', minHeight: 0, minWidth: 0 }}>
                            <Card className={classes.fullHeightCard}>
                                <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                                    {/* Search Field */}
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Search items by name..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className={classes.searchField}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Typography variant="h6" component="h6" color="textSecondary" gutterBottom>
                                        Select Item ({filteredItems.length} items)
                                    </Typography>

                                    {/* Items Grid - Scrollable */}
                                    <Box className={classes.scrollableContent}>
                                        {isLoading ? (
                                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
                                                <CircularProgress />
                                            </Box>
                                        ) : groupedFilteredItems.length > 0 ? groupedFilteredItems.map((group) => (
                                            <Box key={group.label} marginBottom="1.5rem">
                                                <Typography
                                                    variant="subtitle1"
                                                    color="textSecondary"
                                                    style={{ fontWeight: 700, textTransform: "uppercase", marginBottom: "0.75rem" }}
                                                >
                                                    {group.label}
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    {group.items.map((item) => {
                                                        const serviceTypeLabels = getItemServiceTypeLabels(item);

                                                        return (
                                                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                                                <Card
                                                                    className={classes.itemCard}
                                                                    onClick={() => handleItemClick(item)}
                                                                    variant="outlined"
                                                                >
                                                                    <CardContent>
                                                                        <Box display="flex" alignItems="center">
                                                                            <img
                                                                                src={item.imageUrl}
                                                                                alt={item.nameEn}
                                                                                className={classes.itemIcon}
                                                                                onError={(e) => {
                                                                                    e.target.onerror = null;
                                                                                    e.target.src = "https://cdn-icons-png.flaticon.com/512/3003/3003984.png";
                                                                                }}
                                                                            />
                                                                            <Box>
                                                                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                                                                    {item.nameEn}
                                                                                </Typography>
                                                                                <Typography variant="body2" color="textSecondary">
                                                                                    {item.nameAr}
                                                                                </Typography>
                                                                                {serviceTypeLabels.length > 0 && (
                                                                                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: "4px" }}>
                                                                                        {serviceTypeLabels.join(" • ")}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            </Box>
                                        )) : (
                                            <Typography variant="body1" color={errorMsg ? "error" : "textSecondary"}>
                                                {errorMsg ? `Error: ${errorMsg}` : "No items found."}
                                            </Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* RIGHT COLUMN - Order Basket */}
                        <Grid item xs={12} lg={4} style={{ display: 'flex', minHeight: 0, minWidth: 0 }}>
                            <Card className={classes.fullHeightCard}>
                                <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="1rem">
                                        <Typography variant="h5" style={{ fontWeight: 700 }}>Order Basket</Typography>
                                        <Button
                                            size="small"
                                            variant={expressOrder ? "contained" : "outlined"}
                                            color="primary"
                                            onClick={() => handleExpressChange(!expressOrder)}
                                            aria-pressed={expressOrder}
                                            style={{
                                                minWidth: 112,
                                                padding: "4px 10px",
                                                borderRadius: 999,
                                                fontWeight: 700,
                                                justifyContent: "space-between",
                                                letterSpacing: 0
                                            }}
                                        >
                                            <span
                                                style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: "50%",
                                                    backgroundColor: expressOrder ? "#fff" : "#adb5bd",
                                                    display: "inline-block",
                                                    marginRight: 8
                                                }}
                                            />
                                            Express {expressOrder ? "ON" : "OFF"}
                                        </Button>
                                    </Box>

                                    {/* Modern Customer Selection */}
                                    <Box className={classes.selectionBlock} onClick={() => {
                                        if (selectedCustomer) {
                                            setCustomerForm({
                                                mobileNumber: selectedCustomer.mobileNumber || selectedCustomer.phoneNumber || "",
                                                firstName: selectedCustomer.firstName || "",
                                                lastName: selectedCustomer.lastName || "",
                                                emailAddress: selectedCustomer.emailAddress || "",
                                                building: selectedCustomer.building || "",
                                                road: selectedCustomer.road || "",
                                                block: selectedCustomer.block || "",
                                                districtArea: selectedCustomer.districtArea || selectedCustomer.districtName || "",
                                                flatOfficeNo: selectedCustomer.flatOfficeNo || selectedCustomer.flat || "",
                                                shortAddress: selectedCustomer.shortAddress || ""
                                            });
                                        } else {
                                            resetCustomerForm();
                                        }
                                        setCustomerDialogOpen(true);
                                    }}>
                                        <Box display="flex" alignItems="center">
                                            <Person color={selectedCustomer ? "primary" : "disabled"} style={{ marginRight: '12px' }} />
                                            <Box>
                                                <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>Customer Details</Typography>
                                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                    {selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : "Add/Select Customer"}
                                                </Typography>
                                                {selectedCustomer && (
                                                    <Typography variant="caption" style={{ display: 'block', color: theme.palette.primary.main }}>
                                                        {selectedCustomer.mobileNumber || selectedCustomer.phoneNumber}
                                                        {selectedAddress && ` • ${selectedAddress.building}, ${selectedAddress.road}`}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <IconButton size="small"><PersonAdd fontSize="small" /></IconButton>
                                    </Box>

                                    <Divider style={{ margin: '0.75rem 0 1rem' }} />

                                    {/* Basket Items - Scrollable */}
                                    <Box className={classes.scrollableContent}>
                                        {basket.length === 0 ? (
                                            <Typography variant="body2" color="textSecondary" align="center" style={{ padding: "40px 0" }}>
                                                Your basket is empty
                                            </Typography>
                                        ) : (
                                            basket.map((item) => (
                                                <Box key={item.basketId} className={classes.basketItem} display="flex" alignItems="center">
                                                    <Box flex={1}>
                                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                            {item.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {item.serviceName}
                                                        </Typography>
                                                    </Box>

                                                    <Box display="flex" alignItems="center">
                                                        <IconButton size="small" className={classes.qtyBtn} onClick={() => updateQuantity(item.basketId, -1)}>
                                                            <Remove fontSize="small" />
                                                        </IconButton>
                                                        <Typography variant="body2">{item.quantity}</Typography>
                                                        <IconButton size="small" className={classes.qtyBtn} onClick={() => updateQuantity(item.basketId, 1)}>
                                                            <Add fontSize="small" />
                                                        </IconButton>
                                                    </Box>

                                                    <Box width="80px" textAlign="right">
                                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                            {formatBd(item.price * item.quantity)}
                                                        </Typography>
                                                    </Box>

                                                    <IconButton size="small" color="secondary" onClick={() => removeFromBasket(item.basketId)} style={{ marginLeft: "8px" }}>
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))
                                        )}
                                    </Box>

                                    {/* Totals Section */}
                                    <Box>
                                        <Box display="flex" justifyContent="space-between" marginBottom="0.5rem">
                                            <Typography style={{ fontWeight: 500 }}>Subtotal:</Typography>
                                            <Typography style={{ fontWeight: 600 }}>{formatBd(subtotal)}</Typography>
                                        </Box>

                                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="0.5rem">
                                            <Typography variant="body2">Discount (%):</Typography>
                                            <Box display="flex" alignItems="center">
                                                {discountAmount > 0 && (
                                                    <Typography variant="body2" style={{ marginRight: '0.6rem', color: '#888' }}>
                                                        -{formatBd(discountAmount)}
                                                    </Typography>
                                                )}
                                                <TextField
                                                    variant="outlined"
                                                    size="small"
                                                    type="number"
                                                    value={discount}
                                                    onChange={(e) => setDiscount(e.target.value)}
                                                    inputProps={{ min: 0, max: 100, style: { padding: '6px', textAlign: 'right' } }}
                                                    style={{ width: "90px" }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="0.5rem">
                                            <Typography variant="body2">Delivery Charge (BD):</Typography>
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={deliveryCharge}
                                                onChange={(e) => setDeliveryCharge(e.target.value)}
                                                style={{ width: "90px" }}
                                                inputProps={{ style: { padding: '6px', textAlign: 'right' } }}
                                            />
                                        </Box>

                                        {/* Defaults to the configured POS rate; typing here overrides it for this
                                            order only. Placing the order puts it back to the configured value. */}
                                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="0.5rem">
                                            <Typography variant="body2">VAT (%):</Typography>
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={vatPercentage}
                                                onChange={(e) => setVatPercentage(e.target.value)}
                                                inputProps={{ min: 0, max: 100, style: { padding: '6px', textAlign: 'right' } }}
                                                style={{ width: "90px" }}
                                            />
                                        </Box>

                                        <Divider style={{ margin: "0.6rem 0" }} />

                                        <Box display="flex" justifyContent="space-between" alignItems="baseline" marginBottom="0.8rem">
                                            <Box display="flex" alignItems="baseline">
                                                <Typography variant="h5" style={{ fontWeight: 700 }}>Total To Pay:</Typography>
                                                {vatAmount > 0 && (
                                                    <Typography variant="caption" style={{ color: '#888', marginLeft: '0.4rem' }}>
                                                        incl. VAT {formatBd(vatAmount)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography variant="h4" color="primary" style={{ fontWeight: 800 }}>
                                                {formatBd(grandTotal)}
                                            </Typography>
                                        </Box>

                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            size="large"
                                            style={{ padding: '12px', fontWeight: 700, borderRadius: '10px' }}
                                            onClick={handleConfirmOrder}
                                            disabled={isLoading || basket.length === 0}
                                        >
                                            {isLoading ? <CircularProgress size={24} color="inherit" /> : "CONFIRM ORDER"}
                                        </Button>
                                    </Box>

                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>

                {/* Service Type Selection Dialog */}
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <Typography variant="h3" component="div" style={{ fontWeight: 700, textAlign: 'center', marginTop: '10px' }}>
                            {selectedItem?.nameEn}
                        </Typography>
                        <Typography variant="h5" color="textSecondary" style={{ textAlign: 'center', fontWeight: 500 }}>
                            {selectedItem?.nameAr}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Divider style={{ margin: '15px 0' }} />
                        <Typography variant="h6" color="textSecondary" align="center" gutterBottom>
                            Choose Service Type
                        </Typography>
                        <Box display="flex" flexWrap="wrap" justifyContent="center" marginTop="1.5rem" marginBottom="1.5rem">
                            {selectedItemServiceTypes.length > 0 ? selectedItemServiceTypes.map((serviceType) => {
                                const cleanName = getServiceTypeName(serviceType);
                                const basePrice = parsePrice(serviceType.price);
                                const displayPrice = getExpressPrice(basePrice);
                                return (
                                    <Chip
                                        key={serviceType.id}
                                        label={`${cleanName} - ${formatBd(displayPrice)}`}
                                        className={classes.serviceChip}
                                        color="primary"
                                        variant="outlined"
                                        onClick={() => handleServiceSelect(selectedItem, serviceType)}
                                    />
                                );
                            }) : (
                                <Typography variant="body2" color="textSecondary">
                                    No service type pricing is configured for this item.
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)} color="default">
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Confirm Order: Receipt Preview + Payment Method Dialog */}
                <Dialog
                    open={paymentDialogOpen}
                    onClose={() => setPaymentDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ style: { borderRadius: '18px' } }}
                >
                    <DialogTitle style={{ paddingBottom: 0 }}>
                        <Typography variant="h3" style={{ fontWeight: 800, marginTop: '8px' }}>
                            Confirm Order
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Review the receipt and choose how the customer is paying.
                        </Typography>
                    </DialogTitle>
                    <DialogContent style={{ paddingTop: '12px', paddingBottom: '8px' }}>
                        <Grid container spacing={3}>
                            {/* LEFT: Receipt preview */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 700, letterSpacing: '1px', display: 'block', marginBottom: '8px' }}>
                                    RECEIPT PREVIEW
                                </Typography>
                                <Box className={classes.receiptDesk}>
                                    <Box className={classes.receiptPaper}>
                                        <div dangerouslySetInnerHTML={{ __html: buildReceiptBody(buildReceiptData(null)) }} />
                                    </Box>
                                </Box>
                            </Grid>

                            {/* RIGHT: Payment method */}
                            <Grid item xs={12} md={6}>
                                <Box className={classes.totalBanner}>
                                    <Box>
                                        <Typography variant="caption" style={{ color: 'rgba(255,255,255,.85)', fontWeight: 600, letterSpacing: '.5px', display: 'block' }}>
                                            ORDER TOTAL
                                        </Typography>
                                        <Typography style={{ color: '#fff', fontWeight: 800, fontSize: '2rem', lineHeight: 1.1 }}>
                                            {grandTotal.toFixed(3)} <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>BD</span>
                                        </Typography>
                                    </Box>
                                    <Box textAlign="right">
                                        <Typography variant="caption" style={{ color: 'rgba(255,255,255,.85)', display: 'block' }}>
                                            {totalPieces} {totalPieces === 1 ? 'piece' : 'pieces'}
                                        </Typography>
                                        {vatPercent > 0 && (
                                            <Typography variant="caption" style={{ color: 'rgba(255,255,255,.85)', display: 'block' }}>
                                                incl. VAT {vatAmount.toFixed(3)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#32325d', marginBottom: '8px' }}>
                                    Ready / Pickup
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    type="datetime-local"
                                    value={readyDate}
                                    onChange={(e) => { setReadyDate(e.target.value); setReadyDateEdited(true); }}
                                    style={{ marginBottom: '18px' }}
                                    inputProps={{ style: { padding: '10px' } }}
                                />

                                <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#32325d', marginBottom: '14px' }}>
                                    Select Payment Method
                                </Typography>

                                {PAYMENT_METHODS.map((method) => {
                                    const isSelected = selectedPaymentMethod === method.label;
                                    return (
                                        <Box
                                            key={method.label}
                                            className={`${classes.paymentRow} ${isSelected ? classes.paymentRowSelected : ''}`}
                                            onClick={() => setSelectedPaymentMethod(method.label)}
                                        >
                                            <Box className={`${classes.paymentRowIcon} ${isSelected ? classes.paymentRowIconSelected : ''}`}>
                                                {method.icon}
                                            </Box>
                                            <Box flex={1}>
                                                <Typography className={classes.paymentRowLabel}>{method.label}</Typography>
                                                <Typography variant="caption" color="textSecondary">{method.desc}</Typography>
                                            </Box>
                                            <Box className={`${classes.paymentCheck} ${isSelected ? classes.paymentCheckSelected : ''}`}>
                                                <Check style={{ fontSize: 16 }} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{ padding: '12px 24px 24px' }}>
                        <Button onClick={() => setPaymentDialogOpen(false)} color="default" size="large" style={{ fontWeight: 600 }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={!selectedPaymentMethod || isLoading}
                            onClick={() => handlePlaceOrder(selectedPaymentMethod)}
                            style={{ fontWeight: 700, minWidth: '210px', borderRadius: '10px', padding: '12px 20px' }}
                        >
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Order'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Print Receipt Dialog: admin prints the copies they need after the order is saved */}
                <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>
                        <Typography variant="h5" style={{ fontWeight: 700 }}>Order #{printData?.orderNo} saved</Typography>
                        <Typography variant="body2" color="textSecondary">Print the receipts you need.</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" style={{ gap: '12px', paddingTop: '8px', paddingBottom: '8px' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                fullWidth
                                onClick={() => printReceiptCopy("Customer Copy")}
                                style={{ fontWeight: 700, padding: '12px', borderRadius: '10px' }}
                            >
                                Print Customer Copy
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="large"
                                fullWidth
                                onClick={() => printReceiptCopy("Store Copy")}
                                style={{ fontWeight: 700, padding: '12px', borderRadius: '10px' }}
                            >
                                Print Store Copy
                            </Button>
                        </Box>
                    </DialogContent>
                    <DialogActions style={{ padding: '12px 24px 20px' }}>
                        <Button onClick={() => setPrintDialogOpen(false)} color="default" size="large" style={{ fontWeight: 600 }}>
                            Done
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Unified Customer & Address Dialog */}
                <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h4" style={{ fontWeight: 700 }}>Customer Information</Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Contact Details</Typography>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    variant="outlined"
                                    margin="dense"
                                    value={customerForm.mobileNumber}
                                    onChange={(e) => setCustomerForm({ ...customerForm, mobileNumber: e.target.value })}
                                    helperText="Type phone number to auto-populate existing customers"
                                />
                                <Box display="flex" mt={1}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.firstName}
                                        onChange={(e) => setCustomerForm({ ...customerForm, firstName: e.target.value })}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.lastName}
                                        onChange={(e) => setCustomerForm({ ...customerForm, lastName: e.target.value })}
                                    />
                                </Box>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    variant="outlined"
                                    margin="dense"
                                    value={customerForm.emailAddress}
                                    onChange={(e) => setCustomerForm({ ...customerForm, emailAddress: e.target.value })}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Address Details</Typography>
                                <Box display="flex">
                                    <TextField
                                        fullWidth
                                        label="Building"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.building}
                                        onChange={(e) => setCustomerForm({ ...customerForm, building: e.target.value })}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Road"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.road}
                                        onChange={(e) => setCustomerForm({ ...customerForm, road: e.target.value })}
                                    />
                                </Box>
                                <Box display="flex">
                                    <TextField
                                        fullWidth
                                        label="Block"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.block}
                                        onChange={(e) => setCustomerForm({ ...customerForm, block: e.target.value })}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="District/Area"
                                        variant="outlined"
                                        margin="dense"
                                        value={customerForm.districtArea}
                                        onChange={(e) => setCustomerForm({ ...customerForm, districtArea: e.target.value })}
                                    />
                                </Box>
                                <TextField
                                    fullWidth
                                    label="Flat/Office No."
                                    variant="outlined"
                                    margin="dense"
                                    value={customerForm.flatOfficeNo}
                                    onChange={(e) => setCustomerForm({ ...customerForm, flatOfficeNo: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    label="Short Address/Landmark"
                                    variant="outlined"
                                    margin="dense"
                                    value={customerForm.shortAddress}
                                    onChange={(e) => setCustomerForm({ ...customerForm, shortAddress: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{ padding: '16px' }}>
                        <Button onClick={() => setCustomerDialogOpen(false)} color="default">Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!customerForm.mobileNumber || !customerForm.firstName}
                            onClick={handleSaveCustomer}
                        >
                            Confirm Customer Details
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}

export default Pos;
